import { useState, useCallback } from 'react';
import { Input, Button } from '@perch/ui';
import { SUPPORTED_AIRLINE_OPTIONS } from '../../lib/constants';
import { SUPABASE_FUNCTIONS_URL } from '../../lib/supabase';
import type { Session } from '@supabase/supabase-js';

interface PNRLookupFormProps {
  session: Session | null;
  onClose: () => void;
  onLookupSubmitted?: () => void;
}

/**
 * PNR lookup form — enters PNR + Airline + Last Name, submits async lookup.
 * Modal closes after submit, dashboard shows a "Looking up..." pending card.
 *
 * The lookup runs asynchronously on Modal (minutes, not seconds).
 * When it completes, lookup-pnr-callback updates pending_lookups and
 * sends an email. Dashboard picks up the change via Realtime subscription.
 */
export function PNRLookupForm({ session, onClose, onLookupSubmitted }: PNRLookupFormProps) {
  const [pnr, setPnr] = useState('');
  const [airlineCode, setAirlineCode] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = pnr.length >= 5 && airlineCode && lastName.length >= 2;

  const handleSubmit = useCallback(async () => {
    if (!session || !canSubmit) return;
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/lookup-pnr`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pnr: pnr.toUpperCase(),
          airline_code: airlineCode,
          last_name: lastName.trim(),
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Lookup request failed.');
      }

      // Success — modal closes, dashboard shows pending card
      onClose();
      onLookupSubmitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [session, canSubmit, pnr, airlineCode, lastName, onClose, onLookupSubmitted]);

  return (
    <div className="space-y-4 py-2">
      <div className="text-center mb-2">
        <h3 className="text-lg font-medium text-stone-900 dark:text-stone-100 mb-1">
          Look Up Your Booking
        </h3>
        <p className="text-sm text-stone-500 dark:text-stone-400">
          Enter your confirmation code and we'll find the flight details.
        </p>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200/50 dark:border-red-800/30">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <Input
        label="Confirmation Code"
        required
        maxLength={6}
        value={pnr}
        onChange={(e) => setPnr(e.target.value.toUpperCase())}
        placeholder="ABC123"
      />

      <div>
        <label className="block mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">
          Airline <span className="text-primary">*</span>
        </label>
        <select
          required
          className="w-full px-4 py-2.5 border border-stone-300 dark:border-stone-600 rounded-lg bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-[border-color,box-shadow]"
          value={airlineCode}
          onChange={(e) => setAirlineCode(e.target.value)}
        >
          <option value="">Select airline...</option>
          {SUPPORTED_AIRLINE_OPTIONS.map(a => (
            <option key={a.code} value={a.code}>{a.name} ({a.code})</option>
          ))}
        </select>
      </div>

      <Input
        label="Last Name on Booking"
        required
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        placeholder="Smith"
        helperText="The last name as it appears on the ticket."
      />

      {/* Contrail loading bar — animated sweep while lookup is in progress */}
      {isSubmitting && (
        <div className="contrail-loading w-full" />
      )}

      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onClose} fullWidth>
          Cancel
        </Button>
        <Button
          variant="coral"
          onClick={handleSubmit}
          fullWidth
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Look Up Flight'}
        </Button>
      </div>

      <p className="text-xs text-stone-400 dark:text-stone-500 text-center">
        This usually takes a few minutes. We'll show it on your dashboard and email you when it's ready.
      </p>
    </div>
  );
}
